import logger from "@utils/logger"

const FORMAT_REGEX = /{(.*)}/g

export const formatTimestepDate = (value: string) => {
  // 2030-01-01-00:00:00
  return `${value.substring(0, 4)}-${value.substring(5, 7)}-${value.substring(8, 10)}`
}

export const getFormatPrecision = (format: string | undefined) => {
  if (!format) {
    return -1
  }

  const { value: match } = format.matchAll(FORMAT_REGEX).next()

  if (!match) {
    return -1
  }
  const x = match[1]?.matchAll(/\.(\d+)f/g).next()
  if (x && x.value && x.value.length > 1) {
    return Number(x.value[1])
  }

  return -1
}

export const extract_units_without_fstring = function (format: string) {
  // the extract_units_without_fstring does not format the value, it extracts the text part surround the format f-string section
  const regexp = "^([^{]*)({.*})(.*)$" // e.g. USD {,.2f} $
  const split = format.match(regexp)

  let ret = ""
  if (split) {
    ret += split[1] != undefined ? split[1] : ""
    ret += split[3] != undefined ? split[3] : ""
    if (format.indexOf("%") >= 0 && ret.indexOf("%") === -1) ret += "%"
    return ret.trim()
  } else {
    return ""
  }
}

export const formatValuePythonic = (
  value: string | number | d3.NumberValue,
  format?: string,
  wrap: boolean = true,
): string => {
  if (value === null || value === undefined || value === "") {
    if (format) {
      // if blank (e.g. rawresultstable, just send back the format sans value)
      return extract_units_without_fstring(format)
    } else {
      return ""
    }
  }

  if (isNaN(parseFloat(`${value}`))) {
    return ""
  }

  if (!format) {
    return value.toString()
  }

  const { value: match } = format.matchAll(FORMAT_REGEX).next()
  if (!match) return value.toString()

  try {
    // "formatValue" does not apply correctly formatting to negative numbers
    // format positive value and add "-" after formatting (in case of negative value)
    const positiveValue = typeof value === "number" ? Math.abs(value) : value
    let fmt = formatValue(`{value:${match[1]}}`, { value: positiveValue })
    fmt = typeof value === "number" && value < 0 ? `-${fmt}` : fmt

    if (wrap)
      return (
        format.substring(0, match.index) + fmt + format.substring(match.index + match[0].length)
      )
    else return fmt
  } catch (e) {
    // Use old way
    logger.error(value, format, e)
    return value + extract_units_without_fstring(format)
  }
}

// to not override native javascript function `String.prototype.format` code was grabbed
// from https://github.com/jhonmart/python-format-js/blob/master/index.js under permissive MIT license
// revision 5e2aa13 and modified so args_[0] is value to be formatted and args_[1] is object with values
// following changes were made :
// - added LETTER -> case "%": for correct {.2%}
// - fixed {.2%} outputs 21.37% instead of .0
// - fixed error when string was called with toFixed, toExpotential
// - fixed "," thousands separator being applied to decimal part of number - 123,45.6,789 instead of 1,2345.6789
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
const formatValue = (...args_: any[]) => {
  // Create variables
  let self = args_[0]
  const __patterns__ = self.match(/({.*?})/g)
  const { REF, FILL_CHAR, MASK_NUMBER, ALIGN_OP, CROP_SIZE, FRACTION, TYPE_VAR } = {
    REF: 1,
    FILL_CHAR: 2,
    MASK_NUMBER: 3,
    ALIGN_OP: 4,
    CROP_SIZE: 5,
    FRACTION: 7,
    TYPE_VAR: 8,
  }
  const DEFAULT_PLACE = 6
  const ALL_REGEXP =
    /{(\w+)?:([^>\^<\d#]|0)?([#%,])?([>^<\.])?(\d+)?(\.)?(\d+)?([eEfFgGdxXobn#%])?}/g
  const regExpBasic = /{\[?(\w+)\]?}/ // it's not best solution
  // types/use logic
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
  __patterns__?.map((pattern: any, patt_index: number) => {
    const kargs = ALL_REGEXP.exec(pattern) || ALL_REGEXP.exec(pattern)
    const wargs = regExpBasic.exec(pattern)

    // Insert values (one 2 one / array / object)
    const INDEX_VAR = (wargs ? wargs[REF] : kargs ? kargs[REF] : patt_index) || patt_index
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
    const NATUAL_VALUE: any = args_[1][INDEX_VAR]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
    let ACTUAL_VALUE: any = args_[1][INDEX_VAR]

    // Verify sintax/semantic
    if (ACTUAL_VALUE === null || ACTUAL_VALUE === undefined)
      throw new Error(`Replacement index ${INDEX_VAR} out of range for positional args tuple`)
    if (kargs) {
      // If TYPE_VAR is not defined and the first argument is a number, pad a string should from left, so set TYPE_VAR to "d"
      if (kargs[TYPE_VAR] === undefined && typeof ACTUAL_VALUE === "number") {
        kargs[TYPE_VAR] = "d"
      }
      const LETTER =
        (!kargs[FILL_CHAR]
          ? false
          : !kargs[ALIGN_OP] && [..."FfbefoxXn"].includes(kargs[FILL_CHAR].toLowerCase())
          ? kargs[FILL_CHAR]
          : kargs[TYPE_VAR]) || kargs[TYPE_VAR]
      //  padronaze
      if (LETTER) {
        const floatSize = pattern.includes(".")
          ? Number(kargs[FRACTION] || kargs[CROP_SIZE])
          : DEFAULT_PLACE
        switch (LETTER) {
          case "E":
            ACTUAL_VALUE = parseFloat(ACTUAL_VALUE).toExponential(DEFAULT_PLACE).toUpperCase()
            break
          case "e":
            ACTUAL_VALUE = parseFloat(ACTUAL_VALUE).toExponential(DEFAULT_PLACE)
            break
          case "X":
            ACTUAL_VALUE = ACTUAL_VALUE.toString(16).toUpperCase()
            break
          case "x":
            ACTUAL_VALUE = ACTUAL_VALUE.toString(16) // Hexadecimal
            break
          case "b":
            ACTUAL_VALUE = ACTUAL_VALUE.toString(2) // Binary
            break
          case "f":
          case "F":
            ACTUAL_VALUE = parseFloat(ACTUAL_VALUE).toFixed(floatSize)
            break
          case "o":
            ACTUAL_VALUE = ACTUAL_VALUE.toString(8) // Octal
            break
          case "%":
            ACTUAL_VALUE = (NATUAL_VALUE * 100).toFixed(DEFAULT_PLACE)
            break
          default:
            break
        }
        //  mask
        switch (kargs[MASK_NUMBER]) {
          case "#": {
            const MASK = {
              x: "0x",
              X: "0X",
              o: "0o",
              b: "0b",
            }[LETTER]
            ACTUAL_VALUE = MASK + ACTUAL_VALUE
            break
          }
        }
      }
      // signal
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      if ([..." +-,%"].includes(kargs[FILL_CHAR]) && typeof NATUAL_VALUE === "number") {
        ACTUAL_VALUE = ACTUAL_VALUE.toString().replace("-", "")
        if (NATUAL_VALUE >= 0)
          switch (kargs[FILL_CHAR]) {
            case "+":
              ACTUAL_VALUE = "+" + ACTUAL_VALUE
              break
            case " ":
              ACTUAL_VALUE = " " + ACTUAL_VALUE
              break
            case ",":
              const integerPart = ACTUAL_VALUE.toString().split(".")[0] ?? ACTUAL_VALUE.toString()
              const decimalPart = ACTUAL_VALUE.toString().split(".")[1]
              const filledIntegerPart = integerPart.split(/(?=(?:...)*$)/).join(kargs[FILL_CHAR])
              ACTUAL_VALUE = decimalPart ? `${filledIntegerPart}.${decimalPart}` : filledIntegerPart
              break
            case "%":
              kargs[FRACTION]
                ? (ACTUAL_VALUE =
                    (NATUAL_VALUE * 100).toFixed(kargs[FRACTION] as unknown as number) + "%")
                : (ACTUAL_VALUE = (NATUAL_VALUE * 100).toFixed(DEFAULT_PLACE) + "%")
              break
          }
        else ACTUAL_VALUE = "-" + ACTUAL_VALUE
      }
      // space / order / trim
      if (kargs[CROP_SIZE]) {
        ACTUAL_VALUE = ACTUAL_VALUE.toString()
        const FILL_ELEMENT = kargs[FILL_CHAR] || " "
        const SIZE_STRING = ACTUAL_VALUE.length
        const SIZE_ARG = kargs[CROP_SIZE]
        const FILL_LENGTH = SIZE_STRING > SIZE_ARG ? SIZE_STRING : SIZE_ARG
        const FILL = FILL_ELEMENT.repeat(FILL_LENGTH)
        switch (kargs[ALIGN_OP] || kargs[FILL_CHAR]) {
          case "<":
            ACTUAL_VALUE = ACTUAL_VALUE.padEnd(FILL_LENGTH, FILL_ELEMENT)
            break
          case ".": {
            if (LETTER == "%") {
              const a = ACTUAL_VALUE.split(".")
              ACTUAL_VALUE = a[0] + "." + a[1].slice(0, SIZE_ARG) + "%"
            } else if (!(LETTER && /[fF]/.test(LETTER)))
              ACTUAL_VALUE = ACTUAL_VALUE.slice(0, SIZE_ARG)
            break
          }
          case ">":
            ACTUAL_VALUE = ACTUAL_VALUE.padStart(FILL_LENGTH, FILL_ELEMENT)
            break
          case "^": {
            const length_start = Math.floor((FILL_LENGTH - SIZE_STRING) / 2)
            const string_start =
              length_start > 0 ? FILL_ELEMENT.repeat(length_start) + ACTUAL_VALUE : ACTUAL_VALUE

            ACTUAL_VALUE = FILL.replace(RegExp(`.{${string_start.length}}`), string_start)
            break
          }
          default:
            ACTUAL_VALUE = LETTER
              ? ACTUAL_VALUE.padStart(FILL_LENGTH, FILL_ELEMENT)
              : ACTUAL_VALUE.padEnd(FILL_LENGTH, FILL_ELEMENT)
            break
        }
      }
    }
    // SET Definitive value
    self = self.replace(pattern, ACTUAL_VALUE)
  })

  return self
}
